import { type ComponentProps } from 'react';

import {
	Checkbox as ArkCheckbox,
	type CheckboxRootProps as ArkCheckboxRootProps
} from '@ark-ui/react';
import { Check } from '@phosphor-icons/react/dist/csr/Check';
import { Minus } from '@phosphor-icons/react/dist/csr/Minus';

import type { ActionColor, Scale3 } from '../../lib/types';
import { cn } from '../../lib/utils';

type CheckboxProps = Omit<ArkCheckboxRootProps, 'color'> & {
	size?: Scale3;
	variant?: 'classic' | 'surface' | 'soft' | 'outline';
	color?: ActionColor;
};

function CheckboxRoot({
	className,
	size = '2',
	variant = 'surface',
	color = 'accent',
	children,
	...props
}: CheckboxProps) {
	return (
		<ArkCheckbox.Root
			className={cn('pt-checkbox', className)}
			data-size={size}
			data-variant={variant}
			data-accent-color={color}
			{...props}
		>
			{children}
		</ArkCheckbox.Root>
	);
}

function CheckboxControl({ className, ...props }: ComponentProps<typeof ArkCheckbox.Control>) {
	return (
		<ArkCheckbox.Control className={cn('pt-checkbox-control', className)} {...props}>
			<ArkCheckbox.Indicator>
				<Check size={12} weight="bold" />
			</ArkCheckbox.Indicator>
			<ArkCheckbox.Indicator indeterminate>
				<Minus size={12} weight="bold" />
			</ArkCheckbox.Indicator>
		</ArkCheckbox.Control>
	);
}

function CheckboxLabel({ className, ...props }: ComponentProps<typeof ArkCheckbox.Label>) {
	return <ArkCheckbox.Label className={cn('pt-checkbox-label', className)} {...props} />;
}

const Checkbox = Object.assign(CheckboxRoot, {
	Root: CheckboxRoot,
	Control: CheckboxControl,
	Label: CheckboxLabel,
	HiddenInput: ArkCheckbox.HiddenInput
});

export { Checkbox, type CheckboxProps };
